// ブレンドモードの TS 実装 (純関数)。
//
// GLSL 側 (render/shaders/blend.frag.ts) と同じ式を持つ「テストのオラクル」。
// 両者の一致は Playwright のピクセル比較で検証する。
// 値はすべて 0..1 の正規化値、ストレートアルファ。
//
// 分離可能モードは W3C Compositing and Blending Level 1 の式、
// Photoshop 独自のモード (linear burn/dodge、vivid/linear/pin light、hard mix、
// subtract、divide、darker/lighter color) は公開されている一般的な定義を使う。
// soft light は W3C の式を採用する (Photoshop の実装とはわずかに異なる。
// docs/decisions.md 参照)。

import type { BlendMode } from '@/types/document'

export type RGB = [number, number, number]

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const SUM_EPS = 1e-4

function multiply(b: number, s: number) { return b * s }
function screen(b: number, s: number) { return b + s - b * s }
function hardLight(b: number, s: number) { return s <= 0.5 ? multiply(b, 2 * s) : screen(b, 2 * s - 1) }
function colorDodge(b: number, s: number) { return b === 0 ? 0 : s >= 1 ? 1 : Math.min(1, b / (1 - s)) }
function colorBurn(b: number, s: number) { return b >= 1 ? 1 : s <= 0 ? 0 : 1 - Math.min(1, (1 - b) / s) }
function softLight(b: number, s: number) {
  if (s <= 0.5) return b - (1 - 2 * s) * b * (1 - b)
  const d = b <= 0.25 ? ((16 * b - 12) * b + 4) * b : Math.sqrt(b)
  return b + (2 * s - 1) * (d - b)
}
function vividLight(b: number, s: number) { return s <= 0.5 ? colorBurn(b, 2 * s) : colorDodge(b, 2 * s - 1) }

/** 分離可能モード: チャンネルごとの B(cb, cs) */
export function blendChannel(mode: BlendMode, b: number, s: number): number {
  switch (mode) {
    case 'normal':
    case 'dissolve':
      return s
    case 'darken': return Math.min(b, s)
    case 'multiply': return multiply(b, s)
    case 'color-burn': return colorBurn(b, s)
    case 'linear-burn': return clamp01(b + s - 1)
    case 'lighten': return Math.max(b, s)
    case 'screen': return screen(b, s)
    case 'color-dodge': return colorDodge(b, s)
    case 'linear-dodge': return clamp01(b + s)
    case 'overlay': return hardLight(s, b)
    case 'soft-light': return softLight(b, s)
    case 'hard-light': return hardLight(b, s)
    case 'vivid-light': return vividLight(b, s)
    case 'linear-light': return clamp01(b + 2 * s - 1)
    case 'pin-light': return s <= 0.5 ? Math.min(b, 2 * s) : Math.max(b, 2 * s - 1)
    case 'hard-mix': return b + s >= 1 ? 1 : 0
    case 'difference': return Math.abs(b - s)
    case 'exclusion': return b + s - 2 * b * s
    case 'subtract': return clamp01(b - s)
    case 'divide': return s <= 0 ? 1 : clamp01(b / s)
    default:
      throw new Error(`分離不能モード: ${mode}`)
  }
}

// --- 非分離モード (W3C の式) ---
const lum = (c: RGB) => 0.3 * c[0] + 0.59 * c[1] + 0.11 * c[2]

function clipColor(c: RGB): RGB {
  const l = lum(c)
  const n = Math.min(c[0], c[1], c[2])
  const x = Math.max(c[0], c[1], c[2])
  let out: RGB = [c[0], c[1], c[2]]
  if (n < 0) out = out.map(v => l + ((v - l) * l) / (l - n)) as RGB
  if (x > 1) out = out.map(v => l + ((v - l) * (1 - l)) / (x - l)) as RGB
  return out
}

function setLum(c: RGB, l: number): RGB {
  const d = l - lum(c)
  return clipColor([c[0] + d, c[1] + d, c[2] + d])
}

const sat = (c: RGB) => Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2])

function setSat(c: RGB, s: number): RGB {
  const idx = [0, 1, 2].sort((i, j) => c[i]! - c[j]!)
  const [imin, imid, imax] = idx as [number, number, number]
  const out: RGB = [0, 0, 0]
  const cmax = c[imax]!, cmin = c[imin]!, cmid = c[imid]!
  if (cmax > cmin) {
    out[imid] = ((cmid - cmin) * s) / (cmax - cmin)
    out[imax] = s
  } else {
    out[imid] = 0
    out[imax] = 0
  }
  out[imin] = 0
  return out
}

export function isSeparable(mode: BlendMode): boolean {
  return !['hue', 'saturation', 'color', 'luminosity', 'darker-color', 'lighter-color'].includes(mode)
}

/** B(cb, cs) を RGB で返す (分離・非分離の両方) */
export function blendRgb(mode: BlendMode, b: RGB, s: RGB): RGB {
  switch (mode) {
    case 'hue': return setLum(setSat(s, sat(b)), lum(b))
    case 'saturation': return setLum(setSat(b, sat(s)), lum(b))
    case 'color': return setLum(s, lum(b))
    case 'luminosity': return setLum(b, lum(s))
    // 同値のときは下地を選ぶ。GLSL と丸め差が出ないよう許容差を入れる (値は 1/255 刻みなので安全)
    case 'darker-color': return b[0] + b[1] + b[2] <= s[0] + s[1] + s[2] + SUM_EPS ? b : s
    case 'lighter-color': return b[0] + b[1] + b[2] + SUM_EPS >= s[0] + s[1] + s[2] ? b : s
    default:
      return [blendChannel(mode, b[0], s[0]), blendChannel(mode, b[1], s[1]), blendChannel(mode, b[2], s[2])]
  }
}

export interface RGBA {
  r: number
  g: number
  b: number
  a: number
}

/**
 * ストレートアルファでの合成 (W3C の一般合成式)。
 *   co = cs·αs·(1−αb) + cb·αb·(1−αs) + αs·αb·B(cb,cs)
 *   αo = αs + αb·(1−αs)
 * 戻り値はストレート色。αo = 0 のときは色 0。
 * dissolve は呼び出し側が αs を 0 か 1 に量子化して渡す。
 */
export function compositePixel(mode: BlendMode, dst: RGBA, src: RGBA, srcAlphaScale = 1): RGBA {
  const as = clamp01(src.a * srcAlphaScale)
  const ab = clamp01(dst.a)
  const ao = as + ab * (1 - as)
  if (ao <= 0) return { r: 0, g: 0, b: 0, a: 0 }
  const B = blendRgb(mode, [dst.r, dst.g, dst.b], [src.r, src.g, src.b])
  const mix = (cs: number, cb: number, bl: number) => (cs * as * (1 - ab) + cb * ab * (1 - as) + as * ab * bl) / ao
  return {
    r: clamp01(mix(src.r, dst.r, B[0])),
    g: clamp01(mix(src.g, dst.g, B[1])),
    b: clamp01(mix(src.b, dst.b, B[2])),
    a: ao
  }
}

/** 8bit 値へ (四捨五入) */
export function to8(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v * 255)))
}
